/**
 * useModalHistory - Back button closes modal instead of navigating away
 *
 * When a modal opens, pushes a history state. When the user presses the
 * browser back button, the popstate event fires and closes the modal
 * instead of navigating to the previous page.
 *
 * Usage:
 *   const [isOpen, setIsOpen] = useState(false);
 *   useModalHistory(isOpen, () => setIsOpen(false), 'booking');
 */

import { useEffect, useRef } from 'react';

export function useModalHistory(
  isOpen: boolean,
  onClose: () => void,
  modalId: string = 'modal',
): void {
  const pushedRef = useRef(false);

  useEffect(() => {
    if (isOpen && !pushedRef.current) {
      // Push a state so back button has something to pop
      window.history.pushState({ modal: modalId }, '');
      pushedRef.current = true;
    }

    if (!isOpen && pushedRef.current) {
      // Modal was closed programmatically (not via back button)
      // Pop the history entry we pushed so the stack stays clean
      // Only do this if the current state is our modal state
      if (window.history.state?.modal === modalId) {
        window.history.back();
      }
      pushedRef.current = false;
    }
  }, [isOpen, modalId]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePopState = (event: PopStateEvent) => {
      // Back button was pressed while modal was open
      if (pushedRef.current) {
        pushedRef.current = false;
        onClose();
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isOpen, onClose]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pushedRef.current) {
        // Component unmounting with modal open - clean up history
        if (window.history.state?.modal === modalId) {
          window.history.back();
        }
        pushedRef.current = false;
      }
    };
  }, [modalId]);
}
